import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { endUsersApi } from "@/services/endUsersApi";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  User,
  FileText,
  Calendar,
  Mail,
  Phone,
  Image as ImageIcon,
  Activity,
  Clock,
} from "lucide-react";
import { formatDate, formatRelativeTime } from "@/lib/utils";

export function UserDetails() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["end-user", userId],
    queryFn: () => endUsersApi.getEndUserDetails(userId!),
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!data?.user) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">User not found</p>
        <Button onClick={() => navigate("/users")} className="mt-4">
          Back to Users
        </Button>
      </div>
    );
  }

  const user = data.user;
  const passports = (user.passports || []).filter(
    (doc): doc is NonNullable<typeof doc> => Boolean(doc),
  );
  const consents = (user.consents || []).filter(
    (consent): consent is NonNullable<typeof consent> => Boolean(consent),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate("/users")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          User Details
        </h1>
      </div>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">
                Full Name
              </label>
              <p className="text-gray-900 dark:text-gray-100 mt-1">
                {user.full_name || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                <Phone className="h-4 w-4" />
                Mobile Number
              </label>
              <p className="text-gray-900 dark:text-gray-100 mt-1">
                {user.mobile_number}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                <Mail className="h-4 w-4" />
                Email
              </label>
              <p className="text-gray-900 dark:text-gray-100 mt-1">
                {user.email || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">NRIC</label>
              <p className="text-gray-900 dark:text-gray-100 mt-1">
                {user.nric || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Date of Birth
              </label>
              <p className="text-gray-900 dark:text-gray-100 mt-1">
                {user.date_of_birth || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Gender
              </label>
              <p className="text-gray-900 dark:text-gray-100 mt-1">
                {user.gender === "M"
                  ? "Male"
                  : user.gender === "F"
                    ? "Female"
                    : user.gender === "O"
                      ? "Other"
                      : "N/A"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Verification Status */}
      <Card>
        <CardHeader>
          <CardTitle>Verification Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">
                Face Verified
              </label>
              <div className="mt-2">
                {user.is_face_verified ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span>Verified</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-400">
                    <XCircle className="h-5 w-5 mr-2" />
                    <span>Not Verified</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Onboarding Status
              </label>
              <div className="mt-2">
                {user.is_onboarding ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span>Complete</span>
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-600">
                    <XCircle className="h-5 w-5 mr-2" />
                    <span>Incomplete</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Profile Updated
              </label>
              <div className="mt-2">
                {user.is_profile_updated ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span>Yes</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-400">
                    <XCircle className="h-5 w-5 mr-2" />
                    <span>No</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Images */}
      {(user.face_url || passports.some((doc) => doc.document_url)) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Verification Images
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Selfie Image */}
              {user.face_url && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">
                    Selfie Image
                  </label>
                  <div className="border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
                    <img
                      src={user.face_url}
                      alt="User selfie"
                      className="w-full h-auto object-contain max-h-96"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%23f3f4f6"/><text x="50%" y="50%" font-family="Arial" font-size="16" fill="%239ca3af" text-anchor="middle" dominant-baseline="middle">Image not available</text></svg>';
                      }}
                    />
                  </div>
                </div>
              )}

              {/* ID Card Images */}
              {passports.map(
                (doc, index) =>
                  doc.document_url && (
                    <div key={doc._id}>
                      <label className="text-sm font-medium text-gray-500 mb-2 block">
                        {doc.document_type}{" "}
                        {passports.length > 1 ? `(${index + 1})` : ""}
                      </label>
                      <div className="border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
                        <img
                          src={doc.document_url}
                          alt={`${doc.document_type} document`}
                          className="w-full h-auto object-contain max-h-96"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src =
                              'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%23f3f4f6"/><text x="50%" y="50%" font-family="Arial" font-size="16" fill="%239ca3af" text-anchor="middle" dominant-baseline="middle">Image not available</text></svg>';
                          }}
                        />
                      </div>
                    </div>
                  ),
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Service Requests / Consents */}
      {consents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Service Requests ({consents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {consents.map((consent) => (
                <div
                  key={consent._id}
                  className="border rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {consent.servicerequest?.scope_request_type ||
                            "Unknown Type"}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            consent.consent_status === "APPROVED"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : consent.consent_status === "REJECTED"
                                ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                : consent.consent_status === "EXPIRED"
                                  ? "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
                                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                          }`}
                        >
                          {consent.consent_status}
                        </span>
                      </div>
                      {consent.servicerequest?.client && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Requested by:{" "}
                          <span className="font-medium">
                            {consent.servicerequest.client.name}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <label className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Requested
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">
                        {formatRelativeTime(consent.request_at)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(consent.request_at)}
                      </p>
                    </div>
                    {consent.approved_at && (
                      <div>
                        <label className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-600" />
                          Approved
                        </label>
                        <p className="text-gray-900 dark:text-gray-100 mt-1">
                          {formatRelativeTime(consent.approved_at)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(consent.approved_at)}
                        </p>
                      </div>
                    )}
                    {consent.rejected_at && (
                      <div>
                        <label className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <XCircle className="h-3 w-3 text-red-600" />
                          Rejected
                        </label>
                        <p className="text-gray-900 dark:text-gray-100 mt-1">
                          {formatRelativeTime(consent.rejected_at)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(consent.rejected_at)}
                        </p>
                      </div>
                    )}
                    {consent.expired_at && (
                      <div>
                        <label className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Expires
                        </label>
                        <p className="text-gray-900 dark:text-gray-100 mt-1">
                          {formatRelativeTime(consent.expired_at)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(consent.expired_at)}
                        </p>
                      </div>
                    )}
                  </div>

                  {Array.isArray(consent.scope) && consent.scope.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Data Requested
                      </label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {consent.scope.map((field, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200 rounded text-xs"
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {consent.scope_config &&
                    typeof consent.scope_config === "object" &&
                    Object.keys(consent.scope_config).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                          View shared data
                        </summary>
                        <div className="mt-2 pl-4 space-y-1 text-sm">
                          {Object.entries(consent.scope_config).map(
                            ([key, value]) => (
                              <div key={key} className="flex gap-2">
                                <span className="text-gray-500 dark:text-gray-400 min-w-32">
                                  {key}:
                                </span>
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                  {String(value)}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </details>
                    )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          {passports.length > 0 ? (
            <div className="space-y-4">
              {passports.map((doc) => (
                <div key={doc._id} className="border rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Document Type
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">
                        {doc.document_type}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Document Number
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">
                        {doc.document_number || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Country of Origin
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">
                        {doc.country_of_origin || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Nationality
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">
                        {doc.nationality || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Date of Birth
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">
                        {doc.date_of_birth || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Date of Expiry
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">
                        {doc.date_of_expiry || "N/A"}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-500">
                        Address
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 mt-1">
                        {doc.address || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No documents found</p>
          )}
        </CardContent>
      </Card>

      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">
                User ID
              </label>
              <p className="text-gray-900 dark:text-gray-100 mt-1 font-mono text-sm">
                {user._id}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Enrollment ID
              </label>
              <p className="text-gray-900 dark:text-gray-100 mt-1 font-mono text-sm">
                {user.enrollment_id || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Account Status
              </label>
              <p className="text-gray-900 dark:text-gray-100 mt-1 capitalize">
                {user.deletion_status || "active"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Created At
              </label>
              <p className="text-gray-900 dark:text-gray-100 mt-1">
                {user.createdAt ? formatDate(user.createdAt) : "N/A"}
              </p>
            </div>
            {user.updatedAt && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Last Updated
                </label>
                <p className="text-gray-900 dark:text-gray-100 mt-1">
                  {formatDate(user.updatedAt)}
                </p>
              </div>
            )}
            {user.deleted_at && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Deleted At
                </label>
                <p className="text-gray-900 dark:text-gray-100 mt-1">
                  {formatDate(user.deleted_at)}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
